// Exercise-wizard setup for Capture the Flag scenarios.
//
// A CTF scenario (built on the builder's CTF path) is a set of timed multiple-choice questions, so the
// exercise wizard shows a CTF setup instead of the tabletop options: a Format choice (Solo / Competition /
// Team practice) and one "Challenge settings" card. The card's values travel to the launch mapper as one
// object under CTF_SETTINGS_FIELD and become the session's ctfCoordinator — always enabled, always MCQ.

// The builder's CTF workbook writes `Products = "CTF"` on the Info sheet (builder ctf_xlsx.py _info_rows),
// and has since the first CTF workbook, so every published CTF scenario carries it. Products is a comma
// list; the server reads it the same way (ctfCoordinator.js scenarioInfoIsCtf).
export const isCtfScenario = (scenario) => {
  const products = scenario?.info?.Products
  return typeof products === "string" && products.split(",").some((p) => p.trim().toLowerCase() === "ctf")
}

// Details-form key carrying the Challenge settings ({ timeLimitSec, speedScoring, streakBonus, leaderboard,
// sharedEvidence }). Set only for a CTF scenario; the launch mapper gives it precedence over the legacy
// CTF Coordinator fields.
export const CTF_SETTINGS_FIELD = "_ctfExerciseSettings"

export const CTF_TIME_OPTIONS = [
  { value: 60, label: "60 s" },
  { value: 90, label: "90 s" },
  { value: 120, label: "2 min" },
  { value: 0, label: "Untimed" },
]

export const CTF_LEADERBOARD_OPTIONS = [
  { value: "live", label: "Live", hint: "between phases + at the end" },
  { value: "end", label: "End only", hint: "revealed when it's over" },
  { value: "off", label: "Off", hint: "scores stay private" },
]

const STREAK_BONUS_PCT = 0.1

export const CTF_RUN_STYLE_OPTIONS = [
  { value: "live", label: "Live event", hint: "host starts it · everyone together" },
  { value: "selfPaced", label: "Self-paced", hint: "each player at their own pace" },
]

export const CTF_EXERCISE_DEFAULTS = Object.freeze({
  timeLimitSec: 90,
  speedScoring: true,
  streakBonus: true,
  leaderboard: "live",
  sharedEvidence: true,
  // Competition only: a host-paced live event (lobby, host Start, same question for everyone at once)
  // is the default; Solo and Team practice are always self-paced.
  runStyle: "live",
  autoStartWhenAllJoined: false,
  autoAdvance: false,
  // Event presentation overrides (run-of-show spec §10). Empty = use the scenario's own copy.
  eventTitle: "",
  prizeText: "",
  contact: "",
  hideHostedBy: false,
  boardEveryN: null,
  show: null,
  // CTF reports spec §5 (user decision): players' own "Your CTF results" email is off unless the
  // sponsor turns it on for this exercise; sponsors always get "CTF results ready".
  playerResultsEmail: false,
})

export const CTF_EVENT_LIMITS = Object.freeze({ title: 120, prize: 200, prizes: 6, contact: 200 })
export const CTF_EVENT_SHOW_KEYS = [
  { key: "welcome", label: "Welcome" },
  { key: "rules", label: "Rules" },
  { key: "prize", label: "Prizes" },
  { key: "practice", label: "Practice question" },
  { key: "debrief", label: "Debrief" },
  { key: "thanks", label: "Thank-you" },
]
export const CTF_EXERCISE_BOARD_OPTIONS = [
  { value: null, label: "As authored" },
  { value: 2, label: "Every 2" },
  { value: 3, label: "Every 3" },
  { value: 5, label: "Every 5" },
  { value: 0, label: "Round end" },
]

// Plain text only: strip tags/control characters and clamp — the server re-validates (no HTML).
const plainText = (v, max) =>
  String(v ?? "")
    .replace(/<[^>]*>/g, "")
    .split("")
    .filter((ch) => {
      const c = ch.charCodeAt(0)
      return c >= 32 || c === 9 || c === 10 || c === 13
    })
    .join("")
    .trim()
    .slice(0, max)

/** ctfCoordinator.eventMeta from the card's Event presentation values (only what was overridden). */
export const ctfEventMetaFromSettings = (s = {}) => {
  const meta = {}
  const title = plainText(s.eventTitle, CTF_EVENT_LIMITS.title)
  if (title) meta.title = title
  const prizes = String(s.prizeText ?? "")
    .split("\n")
    .map((l) => plainText(l, CTF_EVENT_LIMITS.prize))
    .filter(Boolean)
    .slice(0, CTF_EVENT_LIMITS.prizes)
  if (prizes.length) meta.prizes = prizes
  const contact = plainText(s.contact, CTF_EVENT_LIMITS.contact)
  if (contact) meta.contact = contact
  if (s.hideHostedBy === true) meta.hideHostedBy = true
  if (Number.isInteger(s.boardEveryN) && s.boardEveryN >= 0) meta.boardEveryN = s.boardEveryN
  if (s.show && typeof s.show === "object") {
    const show = {}
    for (const { key } of CTF_EVENT_SHOW_KEYS) if (s.show[key] === false) show[key] = false
    if (Object.keys(show).length) meta.show = show
  }
  return Object.keys(meta).length ? meta : null
}

// Live events need several players ranked against each other — Competition (independent group) only.
export const ctfFormatAllowsLive = (format) => format === "group" || format === "independentGroup"

// Settings to start the card from: an existing exercise's stored coordinator (Edit / Restart / Duplicate),
// else the defaults. Absent keys follow the server's defaults (normalizeCtfCoordinatorConfig), so an
// exercise saved before a setting existed shows what it actually plays with.
export const ctfExerciseSettingsFromCoordinator = (ctfCoordinator) => {
  const c = ctfCoordinator && typeof ctfCoordinator === "object" ? ctfCoordinator : null
  if (!c || c.enabled !== true) return { ...CTF_EXERCISE_DEFAULTS }
  const time = Number(c.timeLimitSec)
  const leaderboardOn = c.leaderboard !== false
  return {
    timeLimitSec: Number.isFinite(time) && time >= 0 ? Math.round(time) : CTF_EXERCISE_DEFAULTS.timeLimitSec,
    speedScoring: c.speedScoring !== false,
    streakBonus: c.streakBonusPct === undefined ? true : Number(c.streakBonusPct) > 0,
    leaderboard: !leaderboardOn ? "off" : c.phaseStandings === false ? "end" : "live",
    sharedEvidence: c.sharedEvidence !== false,
    // An exercise saved before run styles existed is (and stays) self-paced.
    runStyle: c.runStyle === "live" ? "live" : "selfPaced",
    autoStartWhenAllJoined: c.autoStartWhenAllJoined === true,
    autoAdvance: c.autoAdvance === true,
    eventTitle: c.eventMeta?.title || "",
    prizeText: Array.isArray(c.eventMeta?.prizes) ? c.eventMeta.prizes.join("\n") : "",
    contact: c.eventMeta?.contact || "",
    hideHostedBy: c.eventMeta?.hideHostedBy === true,
    boardEveryN: Number.isInteger(c.eventMeta?.boardEveryN) ? c.eventMeta.boardEveryN : null,
    show: c.eventMeta?.show && typeof c.eventMeta.show === "object" ? { ...c.eventMeta.show } : null,
    playerResultsEmail: c.playerResultsEmail === true,
  }
}

// The ctfCoordinator a CTF scenario's exercise is saved with. Always enabled and MCQ: a CTF scenario
// played without the answer tiles matches free text to the nearest option and can mark a right answer
// wrong (the server forces mcqMode for CTF scenarios too).
export const ctfCoordinatorFromExerciseSettings = (settings, { groupGame = false } = {}) => {
  const s = { ...CTF_EXERCISE_DEFAULTS, ...(settings ?? {}) }
  const time = Number(s.timeLimitSec)
  const live = groupGame && s.runStyle === "live"
  return {
    runStyle: live ? "live" : "selfPaced",
    ...(live ? { autoStartWhenAllJoined: s.autoStartWhenAllJoined === true, autoAdvance: s.autoAdvance === true } : {}),
    enabled: true,
    mcqMode: true,
    timeLimitSec: Number.isFinite(time) && time >= 0 ? Math.round(time) : CTF_EXERCISE_DEFAULTS.timeLimitSec,
    speedScoring: s.speedScoring !== false,
    streakBonusPct: s.streakBonus === false ? 0 : STREAK_BONUS_PCT,
    leaderboard: s.leaderboard !== "off",
    phaseStandings: s.leaderboard === "live",
    sharedEvidence: s.sharedEvidence !== false,
    ...(ctfEventMetaFromSettings(s) ? { eventMeta: ctfEventMetaFromSettings(s) } : {}),
    // TODO(ctf-reports P5): the server's normalizeCtfCoordinatorConfig must keep this key; sent only
    // when on so existing payloads are unchanged.
    ...(s.playerResultsEmail === true ? { playerResultsEmail: true } : {}),
  }
}

// One-line summary for the review step.
export const ctfExerciseSettingsSummary = (settings, { format = null } = {}) => {
  const s = { ...CTF_EXERCISE_DEFAULTS, ...(settings ?? {}) }
  const time = CTF_TIME_OPTIONS.find((o) => o.value === Number(s.timeLimitSec))?.label ?? `${s.timeLimitSec} s`
  const board = CTF_LEADERBOARD_OPTIONS.find((o) => o.value === s.leaderboard)?.label ?? "Live"
  const live = format ? ctfFormatAllowsLive(format) && s.runStyle === "live" : null
  return [
    ...(live === null
      ? []
      : [live ? `live event${s.autoStartWhenAllJoined ? " (auto-start)" : ""}${s.autoAdvance ? " · auto-advance" : ""}` : "self-paced"]),
    time === "Untimed" ? "Untimed" : `${time} per question`,
    s.speedScoring !== false ? "speed bonus" : "no speed bonus",
    s.streakBonus !== false ? "streak bonus" : "no streak bonus",
    `leaderboard ${board.toLowerCase()}`,
    s.sharedEvidence !== false ? "evidence panel" : "no evidence panel",
    ...(s.playerResultsEmail === true ? ["players emailed their results"] : []),
  ].join(" · ")
}

// Format cards for a CTF scenario. Same values as the tabletop picker (no data-model change); only the
// wording changes, and the Facilitated Roundtable (a spoken room discussion) is not offered.
const CTF_FORMAT_COPY = {
  single: {
    title: "Solo",
    badge: "1 score",
    subtitle: "one player",
    description: "One person works through the questions on their own — practice, or to try the challenge before you roll it out.",
  },
  solo: {
    title: "Solo",
    badge: "1 score",
    subtitle: "one player",
    description: "One person works through the questions on their own — practice, or to try the challenge before you roll it out.",
  },
  group: {
    title: "Competition",
    badge: "1 score each · leaderboard",
    subtitle: "many players, ranked",
    description: "Each player gets their own copy of the challenge and answers on their own. Everyone is ranked on one leaderboard.",
  },
  independentGroup: {
    title: "Competition",
    badge: "1 score each · leaderboard",
    subtitle: "many players, ranked",
    description: "Each player gets their own copy of the challenge and answers on their own. Everyone is ranked on one leaderboard.",
  },
  multiplayer: {
    title: "Team practice",
    badge: "1 shared team score",
    subtitle: "a team, together, live",
    description:
      "One team works the challenge together and answers as a team — one shared score. There is no leaderboard across teams; use Competition to rank people.",
  },
}

// Review-step "Format" line for a CTF exercise, in the CTF setup's own words (Solo / Competition / Team
// practice) — the tabletop wording ("Independent group") contradicted the format the sponsor just picked.
export const ctfFormatSummary = ({ isGroupSetup, isCollaborative, playersCount }) => {
  const players = `${playersCount} player${playersCount === 1 ? "" : "s"}`
  if (isCollaborative) return `${CTF_FORMAT_COPY.multiplayer?.title || "Team practice"} · ${players}`
  if (isGroupSetup) return `${CTF_FORMAT_COPY.group?.title || "Competition"} · ${players}`
  return "Solo · 1 player"
}

export const ctfFormatOptions = (options) =>
  (options ?? [])
    .filter((o) => o.value !== "facilitated")
    .map((o) => {
      const copy = CTF_FORMAT_COPY[o.value]
      return copy ? { ...o, ...copy, display: copy.title } : o
    })
